import {
  ArrowLeftStartOnRectangleIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  Cog8ToothIcon,
  LanguageIcon,
} from '@heroicons/react/16/solid';
import { useLocalStorageState } from 'ahooks';
import classNames from 'classnames';
import i18next from 'i18next';
import { useEffect, useState } from 'react';
import { Avatar, Button, Dropdown, Loading, Toggle } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { ALERT_CENTER_NS } from 'src/alerts/AlertCenter.constants';
import { useSellerPausedExposureAnalytics } from 'src/components/AlertCenter/AlertCenter.exposure.hook';
import type { tLiveAlert } from 'src/alerts/AlertCenter.types';
import { useAlertCenterContext } from 'src/providers/AlertCenterProvider/AlertCenter.hook';
import AvailabilityStatusPill, {
  AvailabilityStatusDot,
} from 'src/components/AvailabilityStatusPill/AvailabilityStatusPill.component';
import { AVATAR_CORNER_DOT_CLASSNAME } from 'src/components/AvailabilityStatusPill/AvailabilityStatusPill.constants';
import { useAvailabilityStatusLabel } from 'src/components/AvailabilityStatusPill/AvailabilityStatusPill.hook';
import { RouterPaths } from 'src/components/Router/Router.constants.ts';
import { eUserRole } from 'src/providers/UserProvider/User.enums.ts';
import { useUserAvailability } from 'src/hooks/availability/useUserAvailability.hook';
import { useLayoutContext } from '../../../../providers/LayoutProvider/Layout.context.ts';
import { socketManager } from '../../../../providers/SocketsProvider/Socket.singleton.ts';
import { useUserContext } from '../../../../providers/UserProvider/User.context.ts';
import { useWorkspaceContext } from '../../../../providers/WorkspaceProvider/Workspace.context.ts';
import ThemeSelector from '../../../ThemeSwitch/ThemeSwitch.component.tsx';
import { DEFAULT_WORKSPACE_ID } from './SidebarAvatar.constants.ts';
import './SidebarAvatar.styles.scss';

export type tSidebarAvatarProps = {
  /**
   * Opens the shared alert detail for the contextual (P2) alert the avatar
   * marker carries. Optional on purpose: the detail modal lands in a later
   * unit, and `Sidebar.component` keeps mounting `<SidebarAvatar />` unchanged
   * until it does — an un-wired anchor stays operable, it just has nothing to
   * open yet.
   */
  onOpenAlertDetail?: (alert: tLiveAlert) => void;
};

export function SidebarAvatar(props: tSidebarAvatarProps) {
  const [isCopied, setIsCopied] = useState(false);
  const [selectedLang, setSelectedLang] = useLocalStorageState<
    string | undefined
  >('i18nextLng');
  const { onSignOut, currentUser, currentUserRole } = useUserContext();
  const { t } = useTranslation('sidebar/sidebarComponent');

  const { sidebarOpen } = useLayoutContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const [isOpened, setIsOpened] = useState(false);
  // Provider-derived availability (RET-1350): no component-local mirror, so
  // a click can never fire against a stale default before workspaceInfo
  // syncs — the control stays disabled until `isLoaded`.
  const { isActive, isLoaded, isUpdating, setAvailability } =
    useUserAvailability();

  // ── Contextual (P2) alerts (U6) ─────────────────────────────────────────
  // Only `contextual` placements belong on an object; `slot` alerts compete
  // for the navbar and `in-page` ones stay full width. Today that resolves to
  // exactly one declaration (`la-seller-paused`), and the registry — not this
  // component — owns the role gate, so there is one source of truth for who
  // sees it.
  const { alerts, registerInlineAlert } = useAlertCenterContext();
  const { t: tAlert } = useTranslation(ALERT_CENTER_NS);
  const contextualAlert =
    alerts.find((alert) => alert.placement === 'contextual') ?? null;
  const contextualAlertId = contextualAlert?.id ?? null;

  // Claim the alert so the bell badge does not count it a second time. Keyed
  // on the id (not the object) so a re-selection that produces an equal alert
  // does not churn the registration, and the unregister runs on unmount.
  useEffect(() => {
    if (!contextualAlertId) return undefined;
    return registerInlineAlert(contextualAlertId, 'sidebar-avatar');
  }, [contextualAlertId, registerInlineAlert]);

  // RET-1891 — this anchor is on screen without any user action, so mounting
  // it with the seller-paused alert live is the exposure the retired strip
  // used to report. The ledger is shared with the funnel-header chip, so the
  // two anchors emit once between them.
  useSellerPausedExposureAnalytics(contextualAlert);

  useEffect(() => {
    if (selectedLang) i18next.changeLanguage(selectedLang);
  }, [selectedLang]);

  const copyWID = () => {
    navigator.clipboard.writeText(currentWorkspaceId ?? DEFAULT_WORKSPACE_ID);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  const shortName = currentUser
    ? currentUser.name
        .split(' ')
        .map((name) => name[0])
        .join('')
    : '';

  function handleSignOut() {
    onSignOut();
    // Full teardown on sign-out — removeAllListeners is acceptable here
    // because the socket won't be reused after this point.
    socketManager.instance.removeAllListeners();
    socketManager.disconnect();
  }

  function onClickChangeLang(lng: string) {
    i18next.changeLanguage(lng);
    setSelectedLang(lng);
  }

  function onToggleAvailability() {
    if (availabilityDisabled) return;
    setAvailability(!isActive);
  }

  const statusLabel = useAvailabilityStatusLabel(isActive);
  const availabilityDisabled = !isLoaded || isUpdating;

  return (
    <Dropdown className="SidebarAvatar">
      <Dropdown.Toggle
        onClick={() => setIsOpened(!isOpened)}
        className={classNames('SidebarAvatar__Toggle', {
          'SidebarAvatar__Toggle--open': sidebarOpen,
          'SidebarAvatar__Toggle--close': !sidebarOpen,
        })}
        color={'ghost'}
      >
        <div className="relative inline-block">
          <Avatar
            color={'primary'}
            letters={shortName}
            size="xs"
            src={currentUser?.profilePicture ?? undefined}
            shape={'circle'}
            className={'SidebarAvatar__Avatar'}
          />
          {/* No dot until provider truth loads — a default-green dot would
              assert "Activo" for a user who is actually inactive (RET-1350).
              U6 extends this SAME marker rather than adding a second one: a
              live contextual alert re-tones it, renames it and makes it the
              alert's entry point, so exactly one dot ever sits in the corner.
              An alert tone claims nothing about availability, which is why it
              may render before `isLoaded`. */}
          {(isLoaded || contextualAlert !== null) && (
            <AvailabilityStatusDot
              isActive={isActive}
              isAlerting={contextualAlert !== null}
              ariaLabel={
                contextualAlert
                  ? tAlert(contextualAlert.copy.titleKey)
                  : statusLabel
              }
              onActivate={
                contextualAlert
                  ? () => props.onOpenAlertDetail?.(contextualAlert)
                  : undefined
              }
              className={AVATAR_CORNER_DOT_CLASSNAME}
            />
          )}
        </div>
        {sidebarOpen && (
          <div className="SidebarAvatar__Name">{currentUser?.name}</div>
        )}
      </Dropdown.Toggle>
      {isOpened && (
        <Dropdown.Menu
          className={`${sidebarOpen ? 'left-56 ' : 'left-16'} SidebarAvatar__Menu`}
        >
          <Dropdown.Item className="flex">
            <ThemeSelector />
          </Dropdown.Item>
          <Dropdown.Item
            onClick={copyWID}
            className="SidebarAvatar__Menu__Button"
          >
            {isCopied ? (
              <ClipboardDocumentCheckIcon className="SidebarAvatar__Menu__Icon--copied" />
            ) : (
              <ClipboardDocumentIcon className="SidebarAvatar__Menu__Icon" />
            )}
            <div>
              <div>{t('page.avatar.copyWid')}</div>
              <div className="SidebarAvatar__Menu__Workspace">
                {currentWorkspaceId ?? DEFAULT_WORKSPACE_ID}
              </div>
            </div>
          </Dropdown.Item>

          {currentUserRole === eUserRole.SELLER && (
            <Dropdown.Item
              href={RouterPaths.settings}
              className="SidebarAvatar__Menu__Button"
            >
              <Cog8ToothIcon className="SidebarAvatar__Menu__Icon" />
              <p>{t('page.routes.settings')}</p>
            </Dropdown.Item>
          )}

          {/* State-phrased availability row (RET-1350): shows the CURRENT
              state as a pill + switch instead of offering an action
              ("Cambiar a no disponible") the user had to decode. */}
          <Dropdown.Item
            onClick={onToggleAvailability}
            aria-disabled={availabilityDisabled}
            data-testid="availability-status-row"
            className={classNames('SidebarAvatar__Menu__Button', {
              'opacity-50 cursor-not-allowed': availabilityDisabled,
            })}
          >
            {isLoaded ? (
              <div className="flex gap-3 items-center">
                {/* Decorative: the row is the interactive element and the pill
                    announces the state — keep the switch out of the a11y tree. */}
                <Toggle
                  size="sm"
                  color="success"
                  checked={isActive}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                  className="pointer-events-none"
                />
                <div className="flex flex-col items-start gap-0.5">
                  <AvailabilityStatusPill isActive={isActive} />
                  <span className="text-xs text-base-content/70">
                    {isActive
                      ? t('page.avatar.activeStatusMessage')
                      : t('page.avatar.inactiveStatusMessage')}
                  </span>
                </div>
                {isUpdating ? <Loading size="sm" color="primary" /> : null}
              </div>
            ) : (
              // Provider truth not loaded yet — show progress, never a
              // default state that could read as "Activo".
              <Loading size="sm" color="primary" />
            )}
          </Dropdown.Item>

          <Dropdown.Item className="SidebarAvatar__Menu__Button">
            <LanguageIcon className="SidebarAvatar__Menu__Icon" />
            <div className="SidebarAvatar__Menu__Lang">
              <p>{t('page.avatar.language')}</p>
              <div className="SidebarAvatar__Menu__Options">
                <Button
                  variant="link"
                  size="sm"
                  className={
                    selectedLang === 'es' ? 'underline' : 'no-underline'
                  }
                  onClick={() => {
                    onClickChangeLang('es');
                  }}
                >
                  {t('page.avatar.SpanishLanguageOption')}
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className={
                    selectedLang === 'en' ? 'underline' : 'no-underline'
                  }
                  onClick={() => {
                    onClickChangeLang('en');
                  }}
                >
                  {t('page.avatar.EnglishLanguageOption')}
                </Button>
                {/* <button
                  className={selectedLang === 'pt' ? 'underline' : ''}
                  onClick={() => onClickChangeLang('pt')}
                >
                  PT
                </button> */}
              </div>
            </div>
          </Dropdown.Item>
          <Dropdown.Item
            onClick={handleSignOut}
            className="SidebarAvatar__Menu__Button"
          >
            <ArrowLeftStartOnRectangleIcon className="SidebarAvatar__Menu__Icon" />
            <p>{t('page.avatar.exit')}</p>
          </Dropdown.Item>
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
}
